import { EMPTY_OBJ } from '@dadohr/shared/domainTypes/emptyConstants';
import {
  AutomationActionTemplate,
  AutomationActionType,
  ExperienceAnchorType,
  FileResourceType,
  IAutomationActionTemplateAsanaCreateTask,
  IAutomationActionTemplateAtlassianCreateIssue,
  IAutomationActionTemplateCreateDocumentFromTemplate,
  IAutomationActionTemplateDoceboEnroll,
  IAutomationActionTemplateEnrollInExperience,
  IAutomationActionTemplateGoogleAddToGroup,
  IAutomationActionTemplateGoogleCalendarEventInvitation,
  IAutomationActionTemplateHttpPost,
  IAutomationActionTemplateLessonlyAssignToContent,
  IAutomationActionTemplateMicrosoftAddToGroup,
  IAutomationActionTemplateMicrosoftCalendarEventInvitation,
  IAutomationActionTemplateMicrosoftInviteToChannel,
  IAutomationActionTemplateOktaAddToGroup,
  IAutomationActionTemplateSlackInviteToChannel,
  IAutomationActionTemplateSyncToDataDestination,
  IAutomationActionTemplateUnenrollFromExperience,
  IAutomationTemplate,
  IExperienceTemplate,
  SyncToDataDestinationFieldMapping
} from '@dadohr/shared/domainTypes/experience';
import assertNever from 'assert-never';
import { compact, keyBy } from 'lodash';
import { SetStateAction, useCallback } from 'react';
import { UnsafeTFunction, useUnsafeTranslation } from '../../../../../i18next';
import { isSupportingActorMappingValid } from '../../../../../services/experiences/actorUsage';
import { isRequiredCustomField } from '../../../../Org/pages/Integrations/integrations/atlassian/service';
import { TaskBuilderContext } from '../TaskBuilder/types';
import {
  CheckDocumentResponseGoogle,
  CheckDocumentResponseMS
} from './ActionSettings/CreateDocument';
import {
  isSyncDestinationComplete,
  isSyncMappingComplete
} from './ActionSettings/SyncToDataDestination/helpers';

export type AutomationBuilderActionValidationStateItem = {
  error: boolean;
  fields: {
    [name: string]: string | null | undefined;
  };
};

export type AutomationBuilderActionValidationState = {
  [blockId: string]: AutomationBuilderActionValidationStateItem;
};

export const hasValidActionState = (
  s: AutomationBuilderActionValidationState
) => {
  return !Object.values(s).some((b) => b.error);
};

export const updateActionFieldValidation = (
  state: AutomationBuilderActionValidationState,
  actionId: string,
  fieldName: string,
  reason: string | null | undefined,
  propagateInvalidActionState: boolean = true
): AutomationBuilderActionValidationState => {
  const nextFields = {
    ...(state[actionId]?.fields || EMPTY_OBJ),
    [fieldName]: reason
  };
  const isActionValid = !compact(Object.values(nextFields)).length;
  return {
    ...state,
    [actionId]: {
      ...(state[actionId] || EMPTY_OBJ),
      fields: nextFields,
      error: isActionValid
        ? false
        : propagateInvalidActionState
        ? true
        : state[actionId]?.error || false
    }
  };
};

export const updateActionValidation = (
  state: AutomationBuilderActionValidationState,
  actionId: string
) => {
  const actionState = state[actionId];
  if (!actionState) {
    return state;
  }
  return {
    ...state,
    [actionId]: {
      ...actionState,
      error: !!compact(Object.values(actionState.fields)).length
    }
  };
};

type ValidateFn<T> = (
  b: T,
  t: UnsafeTFunction,
  expTpl: TaskBuilderContext,
  a: IAutomationTemplate,
  context?: any
) => string | null | undefined;

type Validator<T extends AutomationActionTemplate> = {
  [field: string]: ValidateFn<T>;
};

// AI-generated automation actions can land with partial `data` (missing
// `group`, `pplToAdd`, `calendar`, `anchor`, …). Optional-chain every
// dereference here so the validator surfaces user-actionable errors
// instead of throwing — same pattern as TaskBuilder/validation.ts. A
// universal try/catch in `validateAction` below covers any new crash
// site we miss.
//
// `hasItems` checks Array.isArray + non-empty. `?.length` alone is
// unsafe because strings also have `.length` — a Firestore field
// accidentally rewritten as `"foo"` instead of `[...]` would pass
// "non-empty" checks even though it's the wrong shape entirely.
const hasItems = (v: unknown): boolean => Array.isArray(v) && v.length > 0;

export const VALIDATORS_OKTA_ADD_TO_GROUP: Validator<IAutomationActionTemplateOktaAddToGroup> = {
  group: (b, t) => {
    if (!b?.group?.id) {
      return t('Please select a group');
    }
  },
  pplToAdd: (b, t) => {
    if (!hasItems(b?.pplToAdd)) {
      return t('Please select who should be added to the group');
    }
  }
};

export const VALIDATORS_GOOGLE_ADD_TO_GROUP: Validator<IAutomationActionTemplateGoogleAddToGroup> = {
  group: (b, t) => {
    if (!b?.group?.id) {
      return t('Please select a group');
    }
  },
  pplToAdd: (b, t) => {
    if (!hasItems(b?.pplToAdd)) {
      return t('Please select who should be added to the group');
    }
  }
};

// The target template isn't available synchronously to a validator — it's
// fetched by the caller (StepTimeline's save handlers) via
// getExperienceTemplateById(id).get() and threaded through as `context`.
export type EnrollInExperienceValidationContext = {
  targetTemplatesByTplId?: { [tplId: string]: IExperienceTemplate };
};

export const VALIDATORS_ENROLL_IN_EXPERIENCE: Validator<IAutomationActionTemplateEnrollInExperience> = {
  actorToEnroll: (b, t) => {
    if (!b?.actorToEnroll) {
      return t('Please select an actor to enroll');
    }
  },
  tplToEnrollIn: (b, t) => {
    if (!b?.tplToEnrollIn) {
      return t('Please select an experience');
    }
  },
  supportingActors: (b, t, expTpl, aTpl, context) => {
    const mappings = b?.supportingActors;
    if (!hasItems(mappings)) {
      return;
    }
    const targetTpl = (context as EnrollInExperienceValidationContext | undefined)
      ?.targetTemplatesByTplId?.[b.tplToEnrollIn];
    const hasInvalidMapping = mappings.some(
      (m) =>
        !isSupportingActorMappingValid(expTpl.supportingActors, m.from) ||
        (!!targetTpl &&
          !isSupportingActorMappingValid(targetTpl.supportingActors, m.to))
    );
    if (hasInvalidMapping) {
      return t('One or more supporting actor mappings need to be updated.');
    }
  }
};

export const VALIDATORS_GOOGLE_CALENDAR_EVENT_INVITATION: Validator<IAutomationActionTemplateGoogleCalendarEventInvitation> = {
  query: (b, t) => {
    if (!b?.query) {
      return t('Event name required');
    }
  },
  calendar: (b, t) => {
    if (!b?.calendar?.id) {
      return t('Please select a Calendar');
    }
  },
  pplToAdd: (b, t) => {
    if (!hasItems(b?.pplToAdd)) {
      return t('Please select who should be added to the event');
    }
  },
  anchor: (b, t, expTpl, aTpl) => {
    if (b?.anchor?.type === 'AFTER_MILESTONE') {
      const experienceTemplateAnchor = expTpl?.anchor;
      if (
        experienceTemplateAnchor?.type === ExperienceAnchorType.EMPLOYEE_DATE
      ) {
        if (experienceTemplateAnchor.field === b.anchor.userField) {
          if (
            experienceTemplateAnchor.offset?.type === 'ABSOLUTE' &&
            aTpl?.timing?.type === 'fixed'
          ) {
            const offset =
              experienceTemplateAnchor.offset.duration < 0
                ? experienceTemplateAnchor.offset.duration * -1
                : experienceTemplateAnchor.offset.duration;
            const lower = b.anchor.lowerBoundary ?? 0;
            if (aTpl.timing.start > offset + lower) {
              return t(
                `This automation would run after the event you're looking for. Move the automation earlier in the timeline or increase the number of days skipped.`
              );
            }
          }
        }
      }
    }
  }
};

export const VALIDATORS_ASANA_CREATE_TASK: Validator<IAutomationActionTemplateAsanaCreateTask> = {
  title: (b, t) => {
    if (!b?.title) {
      return t('Please select a Task Name');
    }
  },
  workspace: (b, t) => {
    if (!b?.workspace?.id) {
      return t('Please select a Workspace');
    }
  },
  project: (b, t) => {
    if (!b?.project?.id) {
      return t('Please select a Project');
    }
  }
};

export const VALIDATORS_ATLASSIAN_CREATE_ISSUE: Validator<IAutomationActionTemplateAtlassianCreateIssue> = {
  project: (b, t) => {
    if (!b?.project?.id) {
      return t('Please select a Project');
    }
  },
  issueType: (b, t) => {
    if (!b?.issueType?.id) {
      return t('Please select an Issue Type');
    }
  },
  summary: (b, t) => {
    if (!b?.summary) {
      return t('Please add a Summary');
    }
  },
  fields: (b, t) => {
    const issueFields = Array.isArray(b?.issueType?.fields)
      ? b!.issueType!.fields
      : [];
    const requiredFields = issueFields.filter((f) => isRequiredCustomField(f));
    if (requiredFields.length) {
      const fieldsByKey = keyBy(
        Array.isArray(b?.fields) ? b!.fields : [],
        (f) => f?.key
      );
      const missingFields: string[] = [];
      requiredFields.forEach((f) => {
        const value = fieldsByKey[f.key]?.value;
        if (value === undefined || value === null || value === '') {
          missingFields.push(f.key);
        }
      });
      if (missingFields.length) {
        const errors = Object.fromEntries(
          missingFields.map((f) => [f, t('Please fill in this field')])
        );
        return JSON.stringify(errors);
      }
    }
  }
};

export const VALIDATORS_LESSONLY_ASSIGN_TO_CONTENT: Validator<IAutomationActionTemplateLessonlyAssignToContent> = {
  content: (b, t) => {
    if (!b?.content?.id) {
      return t('Please select a piece of content');
    }
  },
  pplToAdd: (b, t) => {
    if (!hasItems(b?.pplToAdd)) {
      return t('Please select who should be assigned to a piece of content');
    }
  }
};

export const VALIDATORS_DOCEBO_ENROLL: Validator<IAutomationActionTemplateDoceboEnroll> = {
  target: (b, t) => {
    if (!b.target.id) {
      return t('Please select a course');
    }
  },
  pplToAdd: (b, t) => {
    if (!b.pplToAdd.length) {
      return t('Please select who should be added to a course');
    }
  }
};

export const VALIDATORS_SLACK_INVITE_TO_CHANNEL: Validator<IAutomationActionTemplateSlackInviteToChannel> = {
  pplToAdd: (b, t) => {
    if (!hasItems(b?.pplToAdd)) {
      return t('Please select who should be added to the channel');
    }
  }
};

export const VALIDATORS_MICROSOFT_TEAMS_INVITE_TO_CHANNEL: Validator<IAutomationActionTemplateMicrosoftInviteToChannel> = {
  channelType: (b, t) => {
    if (b?.channel?.kind === 'STANDARD') {
      return t('Please set the channel type to private or shared');
    }
  },
  pplToAdd: (b, t) => {
    if (!hasItems(b?.pplToAdd)) {
      return t('Please select who should be added to the channel');
    }
  }
};

const isValidUrl = (v: string) => {
  try {
    const urlObject = new URL(v);
    return true;
  } catch (e) {
    return false;
  }
};

export const VALIDATORS_CREATE_DOCUMENT_FROM_TEMPLATE: Validator<IAutomationActionTemplateCreateDocumentFromTemplate> = {
  srcTemplate: (b, t, expTpl, autTpl, context) => {
    const src = b?.srcTemplate;
    if (
      !src ||
      (src.type === FileResourceType.MS_ONE_DRIVE &&
        (!src.publicUrl || !isValidUrl(src.publicUrl)))
    ) {
      return t('Please provide a valid URL for the source template document');
    }

    if (src.type === FileResourceType.MS_ONE_DRIVE && src.driveId) {
      return;
    }

    if (context) {
      const response = context as
        | CheckDocumentResponseMS
        | CheckDocumentResponseGoogle;
      if (response?.status === 'Error') {
        console.error('Error validating automation action template', {
          expTpl,
          autTpl,
          actionTpl: b,
          error: response.error
        });
        return src.type === FileResourceType.GOOGLE_DRIVE
          ? t(
              `This URL is not accessible to the Dado integration user or it's not a valid Google Doc.`
            )
          : t(
              `This document is not shared with the Dado integration user or it's not a valid DocX document.`
            );
      }
    }
  },
  target: (b, t) => {
    const target = b?.target;
    if (!target || target.type === 'EMAIL') {
      return t('Invalid target destination type');
    }

    if (!target.resourceId) {
      return t(
        'Please provide a valid target destination for the file to be created.'
      );
    }
  },

  fileNameFormat: (b, t) => {
    if (!b?.fileNameFormat) {
      return t('File name template is required');
    }
  }
  // TODO - make sure that there is at least one participant related variable in here - otherwise filenames are not unique
};

export const VALIDATORS_MICROSOFT_CALENDAR_EVENT_INVITATION: Validator<IAutomationActionTemplateMicrosoftCalendarEventInvitation> = {
  query: (b, t) => {
    if (!b?.query) {
      return t('Event name required');
    }
  },
  calendar: (b, t) => {
    if (!b?.calendar?.id) {
      return t('Please select a Calendar');
    }
  },
  pplToAdd: (b, t) => {
    if (!hasItems(b?.pplToAdd)) {
      return t('Please select who should be added to the event');
    }
  },
  anchor: (b, t, expTpl, aTpl) => {
    if (b?.anchor?.type === 'AFTER_MILESTONE') {
      const experienceTemplateAnchor = expTpl?.anchor;
      if (
        experienceTemplateAnchor?.type === ExperienceAnchorType.EMPLOYEE_DATE
      ) {
        if (experienceTemplateAnchor.field === b.anchor.userField) {
          if (
            experienceTemplateAnchor.offset?.type === 'ABSOLUTE' &&
            aTpl?.timing?.type === 'fixed'
          ) {
            const offset =
              experienceTemplateAnchor.offset.duration < 0
                ? experienceTemplateAnchor.offset.duration * -1
                : experienceTemplateAnchor.offset.duration;
            const lower = b.anchor.lowerBoundary ?? 0;
            if (aTpl.timing.start > offset + lower) {
              return t(
                `This automation would run after the event you're looking for. Move the automation earlier in the timeline or increase the number of days skipped.`
              );
            }
          }
        }
      }
    }
  }
};

export const VALIDATORS_MICROSOFT_ADD_TO_GROUP: Validator<IAutomationActionTemplateMicrosoftAddToGroup> = {
  group: (b, t) => {
    if (!b?.group?.id) {
      return t('Please select a group');
    }
  },
  pplToAdd: (b, t) => {
    if (!hasItems(b?.pplToAdd)) {
      return t('Please select who should be added to the group');
    }
  }
};

export const VALIDATORS_HTTP_POST: Validator<IAutomationActionTemplateHttpPost> = {
  requestUrl: (b, t) => {
    const url = b?.requestUrl;
    if (!url || typeof url !== 'string' || !isValidUrl(url)) {
      return t('Please provide a valid request URL');
    }
  }
};

const VALIDATORS_UNENROLL: Validator<IAutomationActionTemplateUnenrollFromExperience> = {};

export const VALIDATORS_SYNC_TO_DATA_DESTINATION: Validator<IAutomationActionTemplateSyncToDataDestination> = {
  // Keyed as `storages` so the panel reads the message from the same field it
  // already renders; the message covers both the destination and the rows.
  storages: (b, t) => {
    const destination = b?.destination;
    if (!destination || !isSyncDestinationComplete(destination)) {
      return t('Finish configuring the data destination.');
    }
    const mappings = b?.mappings;
    if (!hasItems(mappings)) {
      return t('Add at least one field to sync to the data destination.');
    }
    const allComplete = (mappings as SyncToDataDestinationFieldMapping[]).every(
      isSyncMappingComplete
    );
    if (!allComplete) {
      return t(
        'Each row needs a Dado field and a destination column/field to write to.'
      );
    }
  }
};

/*
@guide Create a new automation action - 2.2 Add validate actions

Add validation checks for your new type.
*/

export const validateAction = (
  s: AutomationBuilderActionValidationState,
  a: AutomationActionTemplate,
  t: UnsafeTFunction,
  expTpl: TaskBuilderContext,
  aTpl: IAutomationTemplate,
  context?: any
) => {
  const validate = <T extends AutomationActionTemplate>(
    validator: Validator<T>,
    block: T
  ) => {
    return Object.entries(validator).reduce((m, [field, v]) => {
      // Universal safety net — same as TaskBuilder/validateBlock. The
      // validator is the bend-back-into-shape boundary for AI-shaped
      // automation actions; a thrown TypeError here would propagate up
      // through validateActions → validateEntireTemplate →
      // onCheckAndLaunch and silently kill Check & Launch with no UI
      // feedback. Catch + log + surface a generic friendly error.
      let result: string | null | undefined;
      try {
        result = v(block, t, expTpl, aTpl, context);
      } catch (err) {
        console.warn(
          `[validateAction] validator threw for ${a.type}.${field} on action ${a.id}; treating as structural error`,
          { err, action: block }
        );
        result = t(
          'This automation action has invalid configuration. Open it to finish the setup, or remove and re-add it.'
        );
      }
      return updateActionFieldValidation(m, a.id, field, result);
    }, s);
  };
  // Outer guard mirrors validateBlock — protect callers from a throw
  // bubbling out of `default: assertNever(a)` when an action has an
  // unknown / missing type (e.g., partially-initialized "just-added"
  // shape). AutomationBuilder calls validateAction directly during the
  // edit flow, so the throw would otherwise crash the dialog.
  if (!a || typeof a !== 'object') {
    console.warn('[validateAction] received null/non-object action', { a });
    return s;
  }
  try {
    switch (a.type) {
      case AutomationActionType.ASANA_CREATE_TASK:
        return validate(VALIDATORS_ASANA_CREATE_TASK, a);
      case AutomationActionType.ATLASSIAN_CREATE_ISSUE:
        return validate(VALIDATORS_ATLASSIAN_CREATE_ISSUE, a);
      case AutomationActionType.OKTA_ADD_TO_GROUP:
        return validate(VALIDATORS_OKTA_ADD_TO_GROUP, a);
      case AutomationActionType.DOCEBO_ENROLL:
        return validate(VALIDATORS_DOCEBO_ENROLL, a);
      case AutomationActionType.ENROLL_IN_EXPERIENCE:
        return validate(VALIDATORS_ENROLL_IN_EXPERIENCE, a);
      case AutomationActionType.GOOGLE_ADD_TO_GROUP:
        return validate(VALIDATORS_GOOGLE_ADD_TO_GROUP, a);
      case AutomationActionType.GOOGLE_CALENDAR_EVENT_INVITATION:
        return validate(VALIDATORS_GOOGLE_CALENDAR_EVENT_INVITATION, a);
      case AutomationActionType.LESSONLY_ASSIGN_TO_CONTENT:
        return validate(VALIDATORS_LESSONLY_ASSIGN_TO_CONTENT, a);
      case AutomationActionType.SLACK_INVITE_TO_CHANNEL:
        return validate(VALIDATORS_SLACK_INVITE_TO_CHANNEL, a);
      case AutomationActionType.MS_OUTLOOK_CALENDER_EVENT_INVITATION:
        return validate(VALIDATORS_MICROSOFT_CALENDAR_EVENT_INVITATION, a);
      case AutomationActionType.MS_OUTLOOK_ADD_TO_GROUP:
        return validate(VALIDATORS_MICROSOFT_ADD_TO_GROUP, a);
      case AutomationActionType.MS_TEAMS_INVITE_TO_CHANNEL:
        return validate(VALIDATORS_MICROSOFT_TEAMS_INVITE_TO_CHANNEL, a);
      case AutomationActionType.CREATE_DOCUMENT_FROM_TEMPLATE:
        return validate(VALIDATORS_CREATE_DOCUMENT_FROM_TEMPLATE, a);
      case AutomationActionType.HTTP_POST:
        return validate(VALIDATORS_HTTP_POST, a);
      case AutomationActionType.UNENROLL_FROM_EXPERIENCE:
        return validate(VALIDATORS_UNENROLL, a);
      case AutomationActionType.SYNC_TO_DATA_DESTINATION:
        return validate(VALIDATORS_SYNC_TO_DATA_DESTINATION, a);
      default:
        return assertNever(a);
    }
  } catch (err) {
    console.warn(
      `[validateAction] unhandled throw or unknown action type (type=${(a as { type?: string }).type}, id=${(a as { id?: string }).id})`,
      err
    );
    return updateActionFieldValidation(
      s,
      (a as { id?: string }).id ?? '<unknown>',
      'structure',
      t(
        'This automation action has an unknown or invalid type. Remove it and re-add it.'
      )
    );
  }
};

export const validateActions = (
  as: AutomationActionTemplate[] | undefined,
  t: UnsafeTFunction,
  expTpl: TaskBuilderContext,
  aTpl: IAutomationTemplate,
  context?: any
) => {
  // `as` can be undefined OR the wrong shape if a write corrupted the
  // automation — coerce to empty before .reduce.
  const safe = Array.isArray(as) ? as : [];
  return safe.reduce(
    (m, b) => validateAction(m, b, t, expTpl, aTpl, context),
    {}
  );
};

export const useFieldValidator = <T extends AutomationActionTemplate>(
  setValidation: React.Dispatch<
    SetStateAction<AutomationBuilderActionValidationState>
  >,
  validator: Validator<T>,
  field: string
) => {
  const { t } = useUnsafeTranslation();
  return useCallback(
    (
      action: T,
      expTpl: TaskBuilderContext,
      aTpl: IAutomationTemplate,
      context?: any
    ) => {
      setValidation((s) =>
        updateActionFieldValidation(
          s,
          action.id,
          field,
          validator[field](action, t, expTpl, aTpl, context)
        )
      );
    },
    [setValidation, validator, field, t]
  );
};
